import { mmFetch } from 'api/mmFetch';
import { mmFetchWithResponse } from 'api/mmFetchWithResponse';
import {
    getCounterpartFacilityApiKeyByDirection,
    normalizeFCTransferDirection,
} from 'components/warehouse/components/fcTransfers/FCTransferFilterConfig';
import dayjs from 'utils/dayjsConfig';
import { fileDownload } from 'utils/fileDownload';

const FC_TRANSFERS_SEARCH_PATH = 'api/inventory/v1/fc-transfers';

const FC_TRANSFERS_TRANSFER_STATUSES_PATH = `${FC_TRANSFERS_SEARCH_PATH}/transfer-statuses`;

/**
 * Jalapeno role id for users with `CREATE_FC_TRANSFER` (Created By filter options).
 * @type {number}
 */
export const FC_TRANSFER_CREATED_BY_ROLE_ID = 1220;

/**
 * Maps UI / route `transferId` to a positive integer path segment for `GET .../fc-transfers/{transferId}`.
 *
 * @param {unknown} value
 * @returns {number | undefined}
 */
export function mapTransferIdToApiPathParam(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
        return value;
    }
    const s = String(value).trim();
    if (!s) return undefined;
    const n = parseInt(s, 10);
    if (!Number.isFinite(n) || n <= 0 || String(n) !== s) {
        return undefined;
    }
    return n;
}

/** @type {readonly string[]} */
const ALLOWED_SORT_FIELDS = Object.freeze(['id', 'createdAt', 'estimatedDepartureDate']);

/** @type {readonly string[]} */
const ALLOWED_SORT_DIRS = Object.freeze(['asc', 'desc']);

/**
 * Normalizes a single-day filter value to `YYYY-MM-DD` for inventory FC transfer search query params.
 * Accepts a plain date string or `{ date: Date | string }` from shared filter transformers (same shapes as `createdOn` / `estimatedDepartureDate`).
 *
 * @param {unknown} value
 * @returns {string | undefined}
 */
function normalizeSingleDayFilterToIsoDate(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    if (typeof value === 'string') {
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
    }
    if (typeof value === 'object' && value !== null && 'date' in value) {
        const d = /** @type {{ date?: Date | string }} */ (value).date;
        if (!d) return undefined;
        const parsed = dayjs(d);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
    }
    return undefined;
}

/**
 * Parses Transfer # filter into numeric `id` for exact-match search only.
 *
 * @param {unknown} value
 * @returns {number | undefined}
 */
function parseTransferIdFilter(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    const s = String(value).trim();
    if (!s) return undefined;
    const n = parseInt(s, 10);
    if (Number.isNaN(n) || String(n) !== s) {
        return undefined;
    }
    return n;
}

/**
 * Builds URLSearchParams for `GET /inventory/v1/fc-transfers`.
 *
 * **Direction (Outbound / Inbound tabs):** The backend does not take a `direction` query param.
 * Tab choice is encoded by which facility filter is set: Outbound sends `sourceFacilityId` = logged-in
 * facility; Inbound sends `destinationFacilityId` = logged-in facility (see
 * `buildRequiredFCTransferListFilters`). Optional counterpart FC is supplied as `counterpartFacilityId`
 * in merged filter state and serialized to `destinationFacilityId` (outbound) or `sourceFacilityId`
 * (inbound). Merged filter objects may still include `direction` for URL/UI state — it is intentionally
 * not serialized here.
 *
 * @param {Object} [params={}]
 * @returns {URLSearchParams}
 */
export function buildFCTransferSearchParams(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.pageSize !== undefined && params.pageSize !== null && params.pageSize !== '') {
        queryParams.set('pageSize', String(params.pageSize));
    }
    if (params.cursor !== undefined && params.cursor !== null && params.cursor !== '') {
        queryParams.set('cursor', String(params.cursor));
    }

    // Outbound: current facility as source. Inbound: current facility as destination. Not `direction=`.
    if (params.sourceFacilityId !== undefined && params.sourceFacilityId !== null && params.sourceFacilityId !== '') {
        queryParams.set('sourceFacilityId', String(params.sourceFacilityId));
    }
    if (
        params.destinationFacilityId !== undefined &&
        params.destinationFacilityId !== null &&
        params.destinationFacilityId !== ''
    ) {
        queryParams.set('destinationFacilityId', String(params.destinationFacilityId));
    }

    // Optional counterpart FC (UI key `counterpartFacilityId`) → API `sourceFacilityId` or `destinationFacilityId`.
    const counterpartId = mapTransferIdToApiPathParam(params.counterpartFacilityId);
    if (counterpartId !== undefined) {
        const counterpartKey = getCounterpartFacilityApiKeyByDirection(normalizeFCTransferDirection(params.direction));
        queryParams.set(counterpartKey, String(counterpartId));
    }

    const transferId = parseTransferIdFilter(params.transferNumber);
    if (transferId !== undefined) {
        queryParams.set('id', String(transferId));
    }

    const createdAt = normalizeSingleDayFilterToIsoDate(params.createdOn);
    if (createdAt) {
        queryParams.set('createdAt', createdAt);
    }

    const estimatedDepartureDate = normalizeSingleDayFilterToIsoDate(params.estimatedDepartureDate);
    if (estimatedDepartureDate) {
        queryParams.set('estimatedDepartureDate', estimatedDepartureDate);
    }

    // TODO: backend will accept multiple statuses; until then send first selection only.
    if (Array.isArray(params.statuses) && params.statuses.length) {
        const first = params.statuses.find((s) => s !== undefined && s !== null && s !== '');
        if (first !== undefined) {
            queryParams.set('status', String(first));
        }
    }

    // TODO: backend will accept created-by identity filters; omit `createdByUserIds` until contract lands.

    const { sortField } = params;
    const { sortDir } = params;
    if (
        sortField &&
        sortDir &&
        ALLOWED_SORT_FIELDS.includes(sortField) &&
        ALLOWED_SORT_DIRS.includes(String(sortDir).toLowerCase())
    ) {
        queryParams.set('sortField', sortField);
        queryParams.set('sortDir', String(sortDir).toLowerCase());
    }

    return queryParams;
}

/**
 * @param {Object} [params={}]
 * @returns {string}
 */
export function buildFCTransferSearchQueryString(params = {}) {
    return buildFCTransferSearchParams(params).toString();
}

/**
 * Search FC transfers (cursor-based list).
 *
 * @param {Object} [params={}] - Search filters and pagination
 * @param {Object} [options={}] - Fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {rows: Array, pagination: Object}}>}
 */
export async function searchFCTransfers(params = {}, options = {}) {
    const qs = buildFCTransferSearchQueryString(params);
    const url = qs.length > 0 ? `${FC_TRANSFERS_SEARCH_PATH}?${qs}` : FC_TRANSFERS_SEARCH_PATH;
    return mmFetchWithResponse(url, options);
}

/**
 * Fetches a single FC transfer with line items (`data.transfer` from API).
 *
 * @param {string | number} transferId - Transfer primary key (must normalize to a positive integer)
 * @param {Object} [options={}] - Fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {transfer: Object}, statusCode?: number}>}
 */
export async function getFCTransferById(transferId, options = {}) {
    const id = mapTransferIdToApiPathParam(transferId);
    if (id === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer id',
            data: {},
            statusCode: null,
        });
    }
    const url = `${FC_TRANSFERS_SEARCH_PATH}/${id}`;
    return mmFetchWithResponse(url, options);
}

/**
 * Fetches allowed FC transfer status strings for filters (`GET .../fc-transfers/transfer-statuses`).
 * Returns `data.statuses` from the API envelope unchanged.
 *
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}]
 * @returns {Promise<string[]>}
 */
export async function fetchFCTransferStatuses(options = {}) {
    const response = await mmFetchWithResponse(FC_TRANSFERS_TRANSFER_STATUSES_PATH, options);
    if (!response?.status) {
        throw new Error(response?.msg ?? 'Failed to fetch FC transfer statuses');
    }
    const statuses = response?.data?.statuses;
    if (!Array.isArray(statuses)) {
        throw new Error('FC transfer statuses response missing statuses array');
    }
    return statuses;
}

/**
 * Fetches Jalapeno accounts with `CREATE_FC_TRANSFER` for the Created By filter
 * (`GET /api/admin/v1/jalacounts/roles/users?roleID=…`).
 *
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}]
 * @returns {Promise<Array<{ id: number, email?: string, firstName?: string, lastName?: string, badgeNumber?: string }>>}
 */
export async function fetchFCTransferCreatedByUsers(options = {}) {
    const url = `api/admin/v1/jalacounts/roles/users?roleID=${FC_TRANSFER_CREATED_BY_ROLE_ID}`;
    const response = await mmFetchWithResponse(url, options);
    if (!response?.status) {
        throw new Error(response?.msg ?? 'Failed to fetch FC transfer creator users');
    }
    const rows = response?.data?.rows;
    if (!Array.isArray(rows)) {
        throw new Error('FC transfer creator users response missing rows array');
    }
    return rows;
}

const FC_TRANSFERS_TRANSFER_INVENTORY_PATH = `${FC_TRANSFERS_SEARCH_PATH}/transfer-inventory`;

/**
 * Fetches lots/locations eligible for transfer assembly at a source facility (`data.transferInventory`).
 *
 * @param {Object} [params={}]
 * @param {number | string} [params.facilityId] - Source facility id (required)
 * @param {Object} [options={}] - Fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {transferInventory?: Array}, statusCode?: number | null}>}
 */
export async function searchAvailableTransferLots(params = {}, options = {}) {
    const { facilityId } = params;
    if (facilityId === undefined || facilityId === null || facilityId === '') {
        return Promise.resolve({
            status: false,
            msg: 'facilityId is required',
            data: {},
            statusCode: null,
        });
    }
    const queryParams = new URLSearchParams();
    queryParams.set('facilityId', String(facilityId));
    const url = `${FC_TRANSFERS_TRANSFER_INVENTORY_PATH}?${queryParams.toString()}`;
    return mmFetchWithResponse(url, options);
}

/**
 * Creates a pending FC transfer (`data.transfer` on success).
 * Body matches inventory v1 contract: `sourceFacilityId`, `destinationFacilityId`,
 * `estimatedDepartureDate` (YYYY-MM-DD), `items` (`lotId`, `locationId`, `numCases`, `numLooseUnits`), optional `notes`.
 *
 * @param {Object} payload - JSON body for `POST /inventory/v1/fc-transfers`
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}] - Extra fetch options (e.g. `{ signal }`); do not pass `body`/`method`
 * @returns {Promise<{status: boolean, msg?: string, data?: {transfer?: Object}, statusCode?: number | null}>}
 */
export async function createFCTransfer(payload, options = {}) {
    return mmFetchWithResponse(FC_TRANSFERS_SEARCH_PATH, {
        ...options,
        method: 'POST',
        body: JSON.stringify(payload ?? {}),
    });
}

/**
 * Adds one line item to a pending FC transfer (`POST .../fc-transfers/{transferId}/item`).
 * Backend requires transfer status `PENDING`.
 *
 * @param {string | number} transferId - Transfer primary key
 * @param {Object} item - `{ lotId, locationId, numCases, numLooseUnits }` (inventory v1 add-item contract)
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}] - Extra fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {transfer?: Object}, statusCode?: number | null}>}
 */
export async function addFCTransferLineItem(transferId, item, options = {}) {
    const id = mapTransferIdToApiPathParam(transferId);
    if (id === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer id',
            data: {},
            statusCode: null,
        });
    }
    const url = `${FC_TRANSFERS_SEARCH_PATH}/${id}/item`;
    return mmFetchWithResponse(url, {
        ...options,
        method: 'POST',
        body: JSON.stringify({ item: item ?? {} }),
    });
}

/**
 * Removes a line item from a pending FC transfer (`DELETE .../fc-transfers/{transferId}/item/{transferItemId}`).
 * Backend requires transfer status `PENDING`.
 *
 * @param {string | number} transferId - Transfer primary key
 * @param {string | number} transferItemId - Transfer line-item row id
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}] - Extra fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {transfer?: Object}, statusCode?: number | null}>}
 */
export async function removeFCTransferLineItem(transferId, transferItemId, options = {}) {
    const id = mapTransferIdToApiPathParam(transferId);
    const itemId = mapTransferIdToApiPathParam(transferItemId);
    if (id === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer id',
            data: {},
            statusCode: null,
        });
    }
    if (itemId === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer item id',
            data: {},
            statusCode: null,
        });
    }
    const url = `${FC_TRANSFERS_SEARCH_PATH}/${id}/item/${itemId}`;
    return mmFetchWithResponse(url, {
        ...options,
        method: 'DELETE',
    });
}

/**
 * Cancels a pending FC transfer (`PUT .../fc-transfers/{transferId}/cancel`).
 *
 * @param {string | number} transferId - Transfer primary key
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}] - Extra fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {transfer?: Object}, statusCode?: number | null}>}
 */
export async function cancelFCTransfer(transferId, options = {}) {
    const id = mapTransferIdToApiPathParam(transferId);
    if (id === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer id',
            data: {},
            statusCode: null,
        });
    }
    const url = `${FC_TRANSFERS_SEARCH_PATH}/${id}/cancel`;
    return mmFetchWithResponse(url, {
        ...options,
        method: 'PUT',
    });
}

/**
 * Downloads a transfer Bill of Lading PDF (`POST .../fc-transfers/{transferId}/bol`).
 *
 * @param {string | number} transferId - Transfer primary key
 * @param {Object} bolFormData - BOL metadata payload
 * @param {string} bolFormData.pickupDateTime
 * @param {string} bolFormData.deliveryDateTime
 * @param {string} bolFormData.setTemp
 * @param {string} bolFormData.carrierName
 * @param {string} [bolFormData.pickupSpecialInstructions]
 * @param {string} [bolFormData.deliverySpecialInstructions]
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}] - Extra fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg: string, data: Object}>}
 */
export async function downloadFCTransferBol(transferId, bolFormData = {}, options = {}) {
    const id = mapTransferIdToApiPathParam(transferId);
    if (id === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer id',
            data: {},
        });
    }

    const url = `${FC_TRANSFERS_SEARCH_PATH}/${id}/bol`;
    const respObj = {
        status: true,
        msg: '',
        data: {},
    };

    try {
        const res = await mmFetch(url, {
            ...options,
            method: 'POST',
            body: JSON.stringify(bolFormData ?? {}),
        });
        if (res.ok) {
            await fileDownload(res, `fc-transfer-${id}-bol.pdf`);
            return respObj;
        }
        const decodedResp = await res.json();
        return {
            status: false,
            msg: decodedResp?.msg ?? 'Failed to download transfer BOL',
            data: decodedResp?.data ?? {},
        };
    } catch (e) {
        return {
            status: false,
            msg: e?.toString?.() ?? 'Failed to download transfer BOL',
            data: {},
        };
    }
}

/**
 * Builds the JSON body for `PUT .../fc-transfers/{id}/item/{transferItemId}/receipt`
 * (`receivedExpiresOn`, `receivedUnitsPerCase`, `receivedQuantities`).
 *
 * @param {Object} payload
 * @param {string | null | undefined} [payload.receivedExpiresOn] - Best-by date `Y-m-d`, or empty/null when absent
 * @param {number | string | undefined} [payload.receivedUnitsPerCase]
 * @param {Array<{ numFullCases?: number | string, numLooseUnits?: number | string }>} [payload.receivedQuantities]
 * @returns {{ receivedExpiresOn: string | null, receivedUnitsPerCase: number, receivedQuantities: Array<{ numFullCases: number, numLooseUnits: number }> }}
 */
export function mapFCTransferReceiptLinePayloadToApiBody(payload = {}) {
    const rawExpires = payload.receivedExpiresOn;
    let receivedExpiresOn = null;
    if (rawExpires !== undefined && rawExpires !== null && rawExpires !== '') {
        receivedExpiresOn = String(rawExpires).trim() || null;
    }

    const rows = Array.isArray(payload.receivedQuantities) ? payload.receivedQuantities : [];
    const receivedQuantities = rows.map((row) => ({
        numFullCases: Math.trunc(Number(row?.numFullCases)) || 0,
        numLooseUnits: Math.trunc(Number(row?.numLooseUnits)) || 0,
    }));

    return {
        receivedExpiresOn,
        receivedUnitsPerCase: Math.trunc(Number(payload.receivedUnitsPerCase)) || 0,
        receivedQuantities,
    };
}

/**
 * Persists one transfer line receipt draft (`PUT .../item/{transferItemId}/receipt`).
 *
 * @param {Object} args
 * @param {string | number} args.transferId - Transfer primary key
 * @param {string | number} args.transferItemId - Transfer line-item row id
 * @param {Object} args.payload - Serialized by {@link mapFCTransferReceiptLinePayloadToApiBody}
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}]
 * @returns {Promise<{status: boolean, msg?: string, data?: Object, statusCode?: number | null}>}
 */
export async function saveFCTransferReceiptLine({ payload, transferId, transferItemId }, options = {}) {
    const id = mapTransferIdToApiPathParam(transferId);
    const itemId = mapTransferIdToApiPathParam(transferItemId);
    if (id === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer id',
            data: {},
            statusCode: null,
        });
    }
    if (itemId === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'Invalid transfer item id',
            data: {},
            statusCode: null,
        });
    }
    const url = `${FC_TRANSFERS_SEARCH_PATH}/${id}/item/${itemId}/receipt`;
    const body = mapFCTransferReceiptLinePayloadToApiBody(payload ?? {});
    return mmFetchWithResponse(url, {
        ...options,
        method: 'PUT',
        body: JSON.stringify(body),
    });
}
